import React, { useEffect, useRef, useState } from 'react';
import { BrowserMultiFormatReader } from '@zxing/browser';
import { useParams, useLocation, useNavigate, Link } from 'react-router-dom';
import GlobalError from '../components/GlobalError';

import axiosInstance from '../utils/axiosInstance';
import {
    Card, CardContent, Stack,
    Typography, Box, Button, IconButton, Divider, TextField
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import SendIcon from '@mui/icons-material/Send';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';

const PickingCheckPage = ({ warehouseId }) => {
    const { id } = useParams();
    const location = useLocation();
    const { orderItem, dataset } = location.state || {};
    const [quantity, setQuantity] = useState('');
    const [message, setMessage] = useState('');
    const [isMatch, setIsMatch] = useState(null); // true, false, null
    const [scanType, setScanType] = useState(null);
    const navigate = useNavigate();
    const videoRef = useRef(null);
    const codeReader = useRef(null);
    const stopDecodeRef = useRef(null);

    useEffect(() => {
        const startCamera = () => {
            if (!videoRef.current) return;

            codeReader.current = new BrowserMultiFormatReader();
            const constraints = {
                video: {
                    facingMode: { ideal: 'environment' },
                    width: { ideal: 640 },
                    height: { ideal: 480 },
                }
            };

            codeReader.current.decodeFromConstraints(constraints, videoRef.current, (result, err) => {
                if (result) {
                    const text = result.getText();
                    if (
                        (scanType === 'qr' &&
                            text.toLowerCase() === orderItem?.variant_id?.toLowerCase()) ||

                        (scanType === 'jan' &&
                            text.toLowerCase() === orderItem?.jan_code?.toLowerCase())
                    ) {
                        setIsMatch(true);
                    } else {
                        setIsMatch(false);
                    }
                }
            }).then((stopFn) => {
                stopDecodeRef.current = stopFn;
            }).catch(console.error);
        };

        const stopCamera = () => {
            if (typeof stopDecodeRef.current === 'function') {
                stopDecodeRef.current();
                stopDecodeRef.current = null;
            }
            if (videoRef.current?.srcObject) {
                videoRef.current.srcObject.getTracks().forEach((track) => track.stop());
                videoRef.current.srcObject = null;
            }
            codeReader.current = null;
        };

        if (isMatch === null && scanType) {
            startCamera();
        } else {
            stopCamera();

            if (isMatch === false) {
                setTimeout(() => {
                    setIsMatch(null);
                }, 3000);
            }
        }

        return () => stopCamera();
    }, [isMatch, orderItem, scanType]);

    const handleQuantityChange = (event) => {
        setQuantity(event.target.value);
    };

    const handleSubmit = async () => {
        if (parseInt(quantity) === orderItem.quantity) {
            try {
                const res = await axiosInstance.post('/picking/update-quantity', {
                    order_item_id: orderItem.order_item_id,
                    quantity,
                    warehouse_id: warehouseId,
                });
                navigate(`/picking/${res.data.order_id}`, {
                    state: {
                        started: true,
                    },
                });
            } catch (err) {
                setMessage('既にピッキング済みです');
                navigate(`/picking`);
            }
        } else {
            setMessage('数量が正しくありません');
        }
    };

    if (!orderItem) {
        return (
            <>
                <Typography>アイテムが見つかりません。</Typography>
                <Link to={`/picking/${id}`}><span className='material-icons color'>arrow_back_ios</span></Link>
            </>
        );
    }

    return (
        <>
            <Stack direction="row" alignItems="center" spacing={1}>
                <IconButton
                    onClick={() =>
                        navigate(`/picking/${id}`, {
                            state: {
                                started: true,
                            },
                        })
                    }
                >
                    <ArrowBackIosNewIcon />
                </IconButton>
                <Typography variant="h1" fontWeight="bold">No.{dataset.order_number}</Typography>
            </Stack>
            <Divider sx={{ my: 2 }} />
            <Card>
                <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                        component="img"
                        src={`${process.env.REACT_APP_IMAGE_BASE_URL}${orderItem.image_url}`}
                        alt={orderItem.product_name}
                        sx={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 1 }}
                        onError={(e) => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.src = `${process.env.REACT_APP_IMAGE_BASE_URL}${process.env.REACT_APP_NOIMAGE}`;
                        }}
                    />
                    <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="body2" color="text.secondary">{orderItem.sku}</Typography>
                        <Typography variant="body1">{orderItem.product_name}</Typography>
                        {orderItem?.option_combination &&
                            Object.entries(orderItem.option_combination).map(([type, value]) => (
                                <Typography key={type} variant="body1">
                                    {type} : {value}
                                </Typography>
                            ))}
                        <Typography variant="body2" color="primary">数量 {orderItem.quantity}個</Typography>
                    </Box>
                </CardContent>
            </Card>
            <Box align='center' sx={{ mt: 4 }}>
                {!scanType && (
                    <Stack spacing={2} alignItems="center">
                        <Typography variant="h4" align='center' >
                            スキャン対象を選んでください
                        </Typography>
                        <Stack direction='row' gap={1}>
                            <Button
                                variant="outlined"
                                fullWidth
                                onClick={() => setScanType('jan')}
                            >
                                JANコード
                            </Button>

                            <Button
                                variant="outlined"
                                fullWidth
                                onClick={() => setScanType('qr')}
                            >
                                QRコード
                            </Button>
                        </Stack>
                    </Stack >
                )}
                {scanType && isMatch !== true && (
                    <>
                        <Typography variant="h4" align='center'>
                            {scanType === 'jan'
                                ? 'JANコードをスキャンしてください'
                                : 'QRコードをスキャンしてください'}
                        </Typography>
                        <Stack
                            direction="row"
                            justifyContent="center"
                        >
                            <Button
                                size="small"
                                variant="text"
                                onClick={() => {
                                    setScanType(null);
                                    setIsMatch(null);
                                }}
                            >
                                スキャン方式を変更
                            </Button>
                        </Stack>
                        <video ref={videoRef} style={{ width: '100%' }} />
                        <Button
                            variant="outlined"
                            endIcon={<QrCodeScannerIcon />}
                            onClick={() => { setIsMatch(true); }}
                            sx={{ mb: 2 }}
                        >
                            スキャンスキップ
                        </Button>
                    </>
                )}
                {isMatch === false && (
                    <Typography color="error" align="center">品番が一致しません</Typography>
                )}
                {isMatch === true && (
                    <>
                        <Typography color="success.main" align="center">品番が一致しました</Typography>
                        <Box>
                            <TextField
                                name='quantity'
                                type="number"
                                label="数量"
                                value={quantity}
                                helperText={message}
                                error={!!message}
                                onChange={handleQuantityChange}
                                InputProps={{ inputProps: { min: 1 } }}
                                sx={{ width: 220, mt: 2, mb: 1 }}
                            />
                        </Box>
                        <Button
                            variant="contained"
                            endIcon={<SendIcon />}
                            onClick={handleSubmit}
                            disabled={!quantity || Number(quantity) < 1}
                            sx={{ width: 180 }}
                        >
                            送信
                        </Button>
                    </>
                )}
            </Box>
        </>
    );
};

export default PickingCheckPage;
